/*
 * Copyright (c) 2014 Macrofocus GmbH. All Rights Reserved.
 */
package org.mkui.canvas

import com.macrofocus.common.collection.arraycopy
import org.mkui.color.MkColor
import org.mkui.color.getUBlue
import org.mkui.color.getUGreen
import org.mkui.color.getURed
import org.mkui.geom.*
import org.mkui.graphics.AbstractIGraphics
import org.mkui.graphics.Edge
import org.mkui.graphics.IGraphics
import org.mkui.graphics.line.BresenhamLineDrawer
import org.mkui.graphics.line.LineDrawer
import kotlin.math.max
import kotlin.math.min

open class AbstractAveragingLayer {
    protected lateinit var overlay: IntArray
    protected lateinit var rgb: Array<FloatArray>
    var width = 0
        protected set
    var height = 0
        protected set

    /**
     * Clip rectangle.
     * cx1,cy1 = upper left corner
     * cx2,cy2 = lower right corner
     *
     * Invariants:
     * cx1 &lt;= cx2
     * cy1 &lt;= cy2
     */
    protected var cx1 = 0
    protected var cy1 = 0
    protected var cx2 = 0
    protected var cy2 = 0
    protected val g: IGraphics = AveragingGraphics()

    private inner class AveragingGraphics : AbstractIGraphics() {
        private var color: MkColor? = null
        val lineVisitor: MoveLineLineVisitor = MoveLineLineVisitor()

        private inner class MoveLineLineVisitor : LineDrawer.LineVisitor {
            override fun process(x: Int, y: Int) {
                if (x >= 0 && y >= 0 && x < width && y < height && color != null) {
                    val i = y * width + x
                    val c = rgb[i]
                    overlay[i]++
                    val r: Float = color!!.getURed().toFloat() / 255f
                    val g: Float = color!!.getUGreen().toFloat() / 255f
                    val b: Float = color!!.getUBlue().toFloat() / 255f
                    c[0] += r
                    c[1] += g
                    c[2] += b
                    val v = c[0] / overlay[i]
                }
            }
        }

        override fun isThreadSafe(): Boolean = false

        override fun getLineWidth(): Double {
            return 0.0
        }

        override fun setLineWidth(lineWidth: Double) {
        }

        override fun setGlobalAlpha(alpha: Double) {}
        override fun getColor(): MkColor {
            return color!!
        }

        override fun setColor(color: MkColor) {
            this@AveragingGraphics.color = color
        }

        override fun draw(geometry: Shape) {
            if (geometry != null) {
                val pi: PathIterator = geometry.getPathIterator()
                val data = FloatArray(6)
                var previousX = 0
                var previousY = 0
                var firstPoint = true
                while (!pi.isDone) {
                    when (pi.currentSegment(data)) {
                        PathIterator.SEG_MOVETO -> {
                            previousX = data[0].toInt()
                            previousY = data[1].toInt()
                            firstPoint = true
                        }
                        PathIterator.SEG_LINETO -> {
                            val currentX = data[0].toInt()
                            val currentY = data[1].toInt()
                            lineDrawer.drawLine(previousX, previousY, currentX, currentY, width, height, lineVisitor, firstPoint)
                            firstPoint = false
                            previousX = currentX
                            previousY = currentY
                        }
                        PathIterator.SEG_CLOSE -> {
                        }
                        else -> throw Error("Segmented type unsupported")
                    }
                    pi.next()
                }
            }
        }

        override fun drawLine(x1: Int, y1: Int, x2: Int, y2: Int) {
            lineDrawer.drawLine(x1, y1, x2, y2, width, height, lineVisitor, true)
        }

        override fun drawLine(x1: Double, y1: Double, x2: Double, y2: Double) {
            drawLine(x1.toInt(), y1.toInt(), x2.toInt(), y2.toInt())
        }

        private fun plot(x: Int, y: Int) {
            lineVisitor.process(x, y)
        }

        private fun plotH(x1: Int, y: Int, x2: Int) {
            for (x in x1 until x2) {
                plot(x, y)
            }
        }

        private fun plotHClipped(x1: Int, y: Int, x2: Int) {
            if (x1 <= cx2 && x2 >= cx1) {
                plotH(if (x1 < cx1) cx1 else x1, y, if (x2 > cx2) cx2 else x2)
            }
        }

        private fun plotV(y1: Int, x: Int, y2: Int) {
            for (y in y1..y2) {
                plot(x, y)
            }
        }

        private fun plotVClipped(y1: Int, x: Int, y2: Int) {
            var y1 = y1
            var y2 = y2
            if (y1 <= cy2 && y2 >= cy1) {
                if (y1 < cy1) {
                    y1 = cy1
                }
                if (y2 > cy2) {
                    y2 = cy2
                }
                for (y in y1..y2) {
                    plot(x, y)
                }
            }
        }

        override fun fill(geometry: Shape) {
            // XXX - Add support for shapes with multiple MOVETO operators and
            // for shapes which aren't closed.
            val b: Rectangle2D = geometry.bounds2D
            val x1 = b.x.toInt()
            val y1 = b.y.toInt()
            val x2 = (b.x + b.width - 1).toInt()
            val y2 = (b.y + b.height - 1).toInt()
            if (x1 > cx2 || y1 > cy2 || x2 < cx1 || y2 < cy1) {
                return
            }

            //if (x1 >= cx1 && x2 <= cx2 && y1 > cy1 && y2 < cy2) {
            val i: PathIterator = geometry.getFlattenPathIterator(flatness)
            while (!i.isDone) {
                var x = IntArray(100)
                var y = IntArray(100)
                var closeX = 0
                var closeY = 0
                var count = 0
                val coords = FloatArray(6)
                var didClose = true
                while (!i.isDone) {
                    when (val segment: Int = i.currentSegment(coords)) {
                        PathIterator.SEG_MOVETO -> {
                            if (!didClose) {
                                x[count] = closeX
                                y[count] = closeY
                                count++
                            }
                            run {
                                closeX = coords[0].toInt()
                                x[count] = closeX
                            }
                            run {
                                closeY = coords[1].toInt()
                                y[count] = closeY
                            }
                            didClose = false
                            count++
                        }
                        PathIterator.SEG_LINETO -> {
                            x[count] = coords[0].toInt()
                            y[count] = coords[1].toInt()
                            didClose = false
                            count++
                        }
                        PathIterator.SEG_CLOSE -> {
                            x[count] = closeX
                            y[count] = closeY
                            count++
                            didClose = true
                        }
                        else -> println("Unsupported type $segment")
                    }
                    // Grow point array if necessary
                    if (count >= x.size) {
                        var tmp = x
                        x = IntArray(x.size * 2)
                        arraycopy(tmp, 0, x, 0, tmp.size)
                        tmp = y
                        y = IntArray(y.size * 2)
                        arraycopy(tmp, 0, y, 0, tmp.size)
                    }
                    if (didClose) {
                        if (!i.isDone) {
                            i.next()
                        }
                        break
                    }
                    i.next()
                }
                if (!didClose) {
                    x[count] = closeX
                    y[count] = closeY
                    count++
                }

                // Is filled with a list of edges for each scan line.
                // The list of edges on an individual scan line is sorted from left to right.
                var edges: Array<Edge?>

                // Initialize edge list with dummy nodes for each scan line.
                // We need the dummy nodes as anchors for the edge lists.
                val n: Int = max(height, width)
                edges = arrayOfNulls(n)
                for (j in 0 until n) {
                    edges[j] = Edge()
                }

                // if (b.width < b.height) {
                //            if (b.width < 20) {
                if (count > 2) {
                    scanFillVertical(edges, x, y, count, max(b.x.toInt(), cx1), min(b.x.toInt() + b.width.toInt() - 1, cx2))
                }
                //            } else {
                //                scanFillHorizontal(x, y, count, Math.max(b.y, cy1), Math.min(b.y + b.height - 1, cy2));
                //            }
            }
        }

        /**
         * ScanFill algorithm for shapes which are wider than tall.
         * @param miny Must be = smallest y and >= cy1.
         * @param maxy Must be = biggest y and <= cy2.
         */
        protected fun scanFillHorizontal(edges: Array<Edge?>, x: IntArray, y: IntArray, cnt: Int, miny: Int, maxy: Int) {
            // Initialize active list with dummy node.
            // We need the dummy node as anchor for the active list.
            val active = Edge()

            // Build the edge list
            buildEdgeListHorizontal(edges, x, y, cnt)

            //Fixme - this is inefficient
//            makePaintPixels(0, miny, width, maxy - miny);
            for (scan in miny..maxy) {
                buildActiveList(edges, scan, active)
                plotHClipped(scan, active)
                updateActiveList(scan, active)
                resortActiveList(active)
                edges[scan]!!.next = null
            }
        }

        /**
         * ScanFill algorithm for shapes which are taller than wide.
         * @param minx Must be = smallest x and >= cx1.
         * @param maxx Must be = biggest x and <= cx2.
         */
        fun scanFillVertical(edges: Array<Edge?>, x: IntArray, y: IntArray, cnt: Int, minx: Int, maxx: Int) {
            // Initialize active list with dummy node.
            // We need the dummy node as anchor for the active list.
            val active = Edge()

            // Build the edge list
            buildEdgeListVertical(edges, x, y, cnt)
            for (scan in minx..maxx) {
                buildActiveList(edges, scan, active)
                plotVClipped(scan, active)
                updateActiveList(scan, active)
                resortActiveList(active)
                edges[scan]!!.next = null
            }
        }

        /** Inserts edge into list in order of increasing edge.intersect  */
        fun insertEdge(list: Edge?, edge: Edge) {
            var p: Edge?
            var q: Edge?
            q = list
            p = q!!.next
            while (p != null) {
                if (edge.intersect < p.intersect) {
                    p = null
                } else {
                    q = p
                    p = p.next
                }
            }
            edge.next = q!!.next
            q.next = edge
        }

        fun buildEdgeListHorizontal(edges: Array<Edge?>, x: IntArray, y: IntArray, cnt: Int) {
            var edge: Edge
            var v1: Int
            var v2: Int
            var yPrev: Int
            yPrev = y[cnt - 2]
            v1 = cnt - 1
            while (v1 >= 1) {
                yPrev = y[v1 - 1]
                if (yPrev != y[v1]) break
                v1--
            }
            var i = 0
            while (i < cnt) {
                v2 = i
                if (y[v1] != y[v2]) {
                    // we only add nonhorizontal lines to the edge list
                    edge = Edge()
                    if (y[v1] < y[v2]) {
                        makeEdgeRecHorizontal(edges, v1, v2, yNext(i, x, y, cnt), edge, x, y)
                    } else {
                        makeEdgeRecHorizontal(edges, v2, v1, yPrev, edge, x, y)
                    }
                    yPrev = y[v1]
                }
                v1 = v2
                i++
            }
        }

        fun buildEdgeListVertical(edges: Array<Edge?>, x: IntArray, y: IntArray, cnt: Int) {
            var edge: Edge
            var v1: Int
            var v2: Int
            var xPrev: Int
            xPrev = x[cnt - 2]
            v1 = cnt - 1
            while (v1 >= 1) {
                xPrev = x[v1 - 1]
                if (xPrev != x[v1]) break
                v1--
            }
            var i: Int = 0
            while (i < cnt) {
                v2 = i
                if (x[v1] != x[v2]) {
                    // we only add nonvertical lines to the edge list
                    edge = Edge()
                    if (x[v1] < x[v2]) {
                        makeEdgeRecVertical(edges, v1, v2, xNext(i, x, y, cnt), edge, x, y)
                    } else {
                        makeEdgeRecVertical(edges, v2, v1, xPrev, edge, x, y)
                    }
                    xPrev = x[v1]
                }
                v1 = v2
                i++
            }
        }

        /** given an index, returns y coordinate of next nonhorizontal line  */
        fun yNext(k: Int, x: IntArray?, y: IntArray, cnt: Int): Int {
            var j: Int
            j = if (k + 1 >= cnt) {
                0
            } else {
                k + 1
            }
            while (y[k] == y[j]) {
                if (j + 1 >= cnt) {
                    j = 0
                } else {
                    j++
                }
            }
            return y[j]
        }

        /** given an index, returns x coordinate of next nonvertical line  */
        fun xNext(k: Int, x: IntArray, y: IntArray?, cnt: Int): Int {
            var j: Int
            j = if (k + 1 >= cnt) {
                0
            } else {
                k + 1
            }
            while (x[k] == x[j]) {
                if (j + 1 >= cnt) {
                    j = 0
                } else {
                    j++
                }
            }
            return x[j]
        }

        fun makeEdgeRecHorizontal(edges: Array<Edge?>, lower: Int, upper: Int, yComp: Int, edge: Edge, x: IntArray, y: IntArray) {
            edge.dPerScan = (x[upper] - x[lower]) / (y[upper] - y[lower]).toFloat()
            edge.intersect = x[lower].toFloat()
            if (y[lower] < cy1) {
                edge.intersect += edge.dPerScan * (cy1 - y[lower])
            }
            if (y[upper] < yComp) {
                // On a monotonically increasing or decreasing edge boundary,
                // shorten the edge by one scan line
                edge.upper = y[upper] - 1
            } else {
                edge.upper = y[upper]
            }
            if (y[lower] <= cy2 && edge.upper >= cy1) {
                //insertEdge(edges[y[lower]], edge);
                insertEdge(edges[max(cy1, y[lower])], edge)
            }
        }

        fun makeEdgeRecVertical(edges: Array<Edge?>, lower: Int, upper: Int, xComp: Int, edge: Edge, x: IntArray, y: IntArray) {
            edge.dPerScan = (y[upper] - y[lower]) / (x[upper] - x[lower]).toFloat()
            edge.intersect = y[lower].toFloat()
            if (x[lower] < cx1) {
                edge.intersect += edge.dPerScan * (cx1 - x[lower])
            }
            if (x[upper] < xComp) {
                // On a monotonically increasing or decreasing edge boundary,
                // shorten the edge by one scan line
                edge.upper = x[upper] - 1
            } else {
                edge.upper = x[upper]
            }
            if (x[lower] <= cx2 && edge.upper >= cx1) {
                //insertEdge(edges[y[lower]], edge);
                insertEdge(edges[max(cx1, x[lower])], edge)
            }
        }

        fun buildActiveList(edges: Array<Edge?>, scan: Int, active: Edge?) {
            var p: Edge?
            var q: Edge?
            p = edges[scan]!!.next
            while (p != null) {
                q = p.next
                insertEdge(active, p)
                p = q
            }
        }

        fun plotHClipped(scan: Int, active: Edge) {
            var p1: Edge?
            var p2: Edge
            p1 = active.next
            while (p1 != null) {
                p2 = p1.next!!
                // Avoid Math.round. It is too expensive!!
                //int x1 = Math.round(p1.intersect);
                //int x2 = Math.round(p2.intersect);
                val x1 = (p1.intersect + 0.5f).toInt()
                val x2 = p2.intersect.toInt()
                plotHClipped(x1, scan, x2)

                // advance to next pair of intersections
                p1 = p2.next!!
            }
        }

        fun plotVClipped(scan: Int, active: Edge) {
            var p1: Edge?
            var p2: Edge
            p1 = active.next
            while (p1 != null) {
                p2 = p1.next!!
                // Avoid Math.round. It is too expensive!!
                //int y1 = Math.round(p1.intersect);
                //int y2 = Math.round(p2.intersect);
                val y1 = (p1.intersect + 0.5f).toInt()
                val y2 = p2.intersect.toInt()
                plotVClipped(y1, scan, y2)

                // advance to next pair of intersections
                p1 = p2.next
            }
        }

        fun updateActiveList(scan: Int, active: Edge) {
            var p: Edge?
            var q: Edge
            q = active
            p = active.next
            while (p != null) {
                if (scan >= p.upper) {
                    p = p.next
                    deleteAfter(q)
                } else {
                    p.intersect += p.dPerScan
                    q = p
                    p = p.next
                }
            }
        }

        protected fun prepareEdgeList(edges: Array<Edge>, scan: Int) {
            var p: Edge?
            var q: Edge
            q = edges[scan]
            p = q.next
            while (p != null) {
                if (scan >= p.upper) {
                    p = p.next
                    deleteAfter(q)
                } else {
                    q = p
                    p = p.next
                }
            }
        }

        fun deleteAfter(q: Edge) {
            val p: Edge = q.next!!
            q.next = p.next
        }

        fun resortActiveList(active: Edge) {
            var p: Edge?
            var q: Edge?
            p = active.next
            active.next = null
            while (p != null) {
                q = p.next
                insertEdge(active, p)
                p = q
            }
        }

        override fun drawPoint(point: Point2D?) {
            if (point != null) {
                lineVisitor.process(point.x.toInt(), point.y.toInt())
            }
        }

        /**
         * Derived from L. Patrick "GraphApp: A Portable Graphics Package Designed for Teaching," Ph.D. Thesis School of Information Technology, University of Sydney 2002.
         */
        override fun fillCircle(rectangle: Rectangle2D) {
            /* e(x,y) = b*b*x*x + a*a*y*y - a*a*b*b */
            if (rectangle.width <= 2 || rectangle.height <= 2) {
                fillRectangle2D(rectangle)
                return
            }
            val a = (rectangle.width / 2).toInt()
            val b = (rectangle.height / 2).toInt()
            var x = 0
            var y = b
            val a2 = (a * a).toLong()
            val b2 = (b * b).toLong()
            val xcrit = 3 * a2 / 4 + 1
            val ycrit = 3 * b2 / 4 + 1
            var t = b2 + a2 - 2 * a2 * b /* t = e(x+1,y-1) */
            var dxt = b2 * (3 + x + x)
            var dyt = a2 * (3 - y - y)
            val d2xt = b2 + b2
            val d2yt = a2 + a2
            val r1: Rectangle = Rectangle(
                rectangle.x.toInt() + a, rectangle.y.toInt(),
                rectangle.width.toInt() and 1,  /* i.e. if width is odd */
                1
            )
            val r2: Rectangle = Rectangle(
                rectangle.x.toInt() + a, (rectangle.y.toInt() + rectangle.height.toInt()) - 1,
                rectangle.width.toInt() and 1,  /* i.e. if width is odd */
                1
            )
            while (y > 0) {
                when {
                    t + a2 * y < xcrit -> { /* e(x+1,y-1/2) <= 0 */
                        /* move outwards to encounter edge */
                        x += 1
                        t += dxt
                        dxt += d2xt

                        /* move outwards */r1.ix -= 1
                        r1.iwidth += 2
                        r2.ix -= 1
                        r2.iwidth += 2
                    }
                    t - b2 * x >= ycrit -> { /* e(x+1/2,y-1) > 0 */
                        /* drop down one line */
                        y -= 1
                        t += dyt
                        dyt += d2yt

                        /* enlarge rectangles */r1.iheight += 1
                        r2.iheight += 1
                        r2.iy -= 1
                    }
                    else -> {
                        /* drop diagonally down and out */
                        x += 1
                        y -= 1
                        t += dxt + dyt
                        dxt += d2xt
                        dyt += d2yt
                        if (r1.width > 0 && r1.height > 0) {
                            /* draw rectangles first */
                            if (r1.y + r1.height < r2.y) {
                                /* distinct rectangles */
                                fillRectangle2D(r1)
                                fillRectangle2D(r2)
                            }

                            /* move down */r1.iy += r1.height.toInt()
                            r1.iheight = 1
                            r2.iy -= 1
                            r2.iheight = 1
                        } else {
                            /* skipped pixels on initial diagonal */

                            /* enlarge, rather than moving down */
                            r1.iheight += 1
                            r2.iheight += 1
                            r2.iy -= 1
                        }

                        /* move outwards */r1.ix -= 1
                        r1.iwidth += 2
                        r2.ix -= 1
                        r2.iwidth += 2
                    }
                }
            }
            if (r1.y < r2.y) {
                /* overlap */
                r1.ix = rectangle.x.toInt()
                r1.iwidth = rectangle.width.toInt()
                r1.iheight = (r2.y + r2.height - r1.y).toInt()
                fillRectangle2D(r1)
            } else if (x <= a) {
                /* crossover, draw final line */
                r1.ix = rectangle.x.toInt()
                r1.iwidth = rectangle.width.toInt()
                r1.iheight = (r1.y + r1.height - r2.y).toInt()
                r1.iy = r2.y.toInt()
                fillRectangle2D(r1)
            }
        }

        fun horizontalLine(x1: Int, y: Int, x2: Int) {
            for (x in x1..x2) {
                lineVisitor.process(x, y)
            }
        }

        override fun fillRectangle2D(rectangle: Rectangle2D) {
            val x2 = (rectangle.x + rectangle.width).toInt() - 1
            for (y in 0 until rectangle.height.toInt()) {
                horizontalLine(rectangle.x.toInt(), (rectangle.y + y).toInt(), x2)
            }
        }

        override fun translate(x: Int, y: Int) {
            throw UnsupportedOperationException()
        }

        override fun rotate(theta: Double) {
            throw UnsupportedOperationException()
        }

        override fun beginPath() {
            throw UnsupportedOperationException()
        }

        override fun moveTo(x: Double, y: Double) {
            throw UnsupportedOperationException()
        }

        override fun lineTo(x: Double, y: Double) {
            throw UnsupportedOperationException()
        }

        override fun closePath() {
            throw UnsupportedOperationException()
        }

        override fun stroke() {
            throw UnsupportedOperationException()
        }

        override fun fill() {
            throw UnsupportedOperationException()
        }
    }

    companion object {
        private val lineDrawer: LineDrawer = BresenhamLineDrawer()

        /** Flatness is used for flattening general paths.  */
        private const val flatness = 0.33333
    }
}